import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { getPrivateIconUrl } from '../../../../config/urls';
import { computeOptimalColorFromBackgroundHex, hexToHSL, HSLToHex } from '../../../../utils/color';
import { LineUpJerseyIconSvg } from '../../assets/svg';

const FALLBACK_COLOR = '#cccccc';

export enum LineUpPlayerJerseySize {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export enum LineUpPlayerNumberSize {
  small = 'small',
  medium = 'medium',
}

type SIZESType = {
  fontSize: string;
};

const SIZES: Record<LineUpPlayerJerseySize, SIZESType> = {
  [LineUpPlayerJerseySize.small]: {
    fontSize: '105%',
  },
  [LineUpPlayerJerseySize.medium]: {
    fontSize: '120%',
  },
  [LineUpPlayerJerseySize.large]: {
    fontSize: '120%',
  },
};

const NUMBER_SIZE: Record<LineUpPlayerNumberSize, string> = {
  [LineUpPlayerNumberSize.small]: '0.9em',
  [LineUpPlayerNumberSize.medium]: '1em',
};

type WrapperType = {
  $jerseySize: LineUpPlayerJerseySize;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  display: inline-flex;

  ${({ $jerseySize }) => css`
    font-size: ${SIZES[$jerseySize].fontSize};
  `}
`;

type JerseyWrapperType = {
  $jerseyColor?: string;
  $isGoalKeeper?: boolean;
};

const JerseyWrapper = styled.div<JerseyWrapperType>`
  position: relative;
  display: inline-flex;

  ${({ $jerseyColor, $isGoalKeeper }) => {
    const isColorValid = /^#[a-zA-Z0-9]{6}$/.test($jerseyColor ?? '');
    const { h, s, l } = hexToHSL(isColorValid ? $jerseyColor : FALLBACK_COLOR);
    const [nextH, nextS, nextL] = [h, s * 0.8, l < 40 ? 40 : l];
    const hex = HSLToHex(nextH, nextS, nextL);
    const numberColor = $isGoalKeeper
      ? '#000000'
      : computeOptimalColorFromBackgroundHex(isColorValid ? hex : FALLBACK_COLOR);

    return css`
      --jersey-main-color: hsl(${nextH}deg, ${nextS}%, ${nextL}%);
      --jersey-number-color: ${numberColor};
      --jersey-border-color: ${nextL === 40 ? 'transparent' : numberColor};
    `;
  }}
`;

const sharedJerseyCss = css`
  height: 2.5725em;
  width: 2.5725em;

  display: flex;
  align-items: center;
  justify-content: center;

  & .line-up-jersey {
    fill: var(--jersey-main-color);
  }

  & .line-up-jersey-border {
    fill: transparent;
  }
`;

type JerseyImgIconType = {
  $imgUrl: string;
};

const JerseyImgIcon = styled.div<JerseyImgIconType>`
  ${sharedJerseyCss}
  ${({ $imgUrl }) => {
    return css`
      background-image: url(${$imgUrl});
    `;
  }}
  background-repeat: no-repeat;
  background-size: contain;
`;

const JerseySvgWrapper = styled.div`
  ${sharedJerseyCss}
`;

const JerseyIcon = styled(LineUpJerseyIconSvg)`
  position: absolute;
`;

type NumberType = {
  $numberColor?: string;
  $numberOutlineColor?: string;
  $size: LineUpPlayerNumberSize;
};

const Number = styled.div<NumberType>`
  z-index: 1;
  letter-spacing: 0.06em;
  text-indent: 0.06em;
  font-weight: bold;
  ${({ $numberColor, $numberOutlineColor, $size }) => {
    return css`
      color: ${$numberColor ?? 'var(--jersey-number-color)'};
      font-size: ${NUMBER_SIZE[$size]};
      text-shadow: ${$numberOutlineColor
        ? `-0.06em -0.06em 0 ${$numberOutlineColor},
        0.06em -0.06em 0 ${$numberOutlineColor},
        -0.06em 0.06em 0 ${$numberOutlineColor},
        0.06em 0.06em 0 ${$numberOutlineColor},
        -0.12em 0 0 ${$numberOutlineColor},
        0.12em 0 0 ${$numberOutlineColor},
        0 -0.12em 0 ${$numberOutlineColor},
        0 0.12em 0 ${$numberOutlineColor}`
        : undefined};
    `;
  }}
`;

type LineUpPlayerJerseyProps = {
  className?: string;
  children?: React.ReactNode;
  number?: number;
  jerseyColor?: string;
  jerseyImg?: string;
  numberColor?: string;
  numberOutlineColor?: string;
  size?: LineUpPlayerJerseySize;
  numberSize?: LineUpPlayerNumberSize;
};

const LineUpPlayerJersey = ({
  className = '',
  children,
  number,
  jerseyColor,
  jerseyImg,
  numberColor,
  numberOutlineColor,
  size = LineUpPlayerJerseySize.medium,
  numberSize = LineUpPlayerNumberSize.medium,
}: LineUpPlayerJerseyProps) => {
  const displayedJersey = useMemo(() => {
    if (jerseyImg) {
      return (
        <JerseyImgIcon $imgUrl={getPrivateIconUrl(jerseyImg)}>
          <Number $numberColor={numberColor} $numberOutlineColor={numberOutlineColor} $size={numberSize}>
            {number}
          </Number>
        </JerseyImgIcon>
      );
    }

    return (
      <JerseySvgWrapper>
        <JerseyIcon />
        <Number $numberColor={numberColor} $numberOutlineColor={numberOutlineColor} $size={numberSize}>
          {number}
        </Number>
      </JerseySvgWrapper>
    );
  }, [jerseyImg, numberColor, numberOutlineColor, number, numberSize]);

  return (
    <Wrapper className={className} $jerseySize={size}>
      <JerseyWrapper $jerseyColor={jerseyColor}>{displayedJersey}</JerseyWrapper>
      {children}
    </Wrapper>
  );
};

export default LineUpPlayerJersey;
